import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  resetParameters,
  setLastSearchSegments,
  setPendingSegments,
  setQuery,
} from "../store/searchPage";
import { parametersToSegments } from "../lib/search/parametersToSegments";
import { parametersToQueryText } from "../lib/search/parametersToQueryText";

/**
 * Shared "apply a data-catalog selection" flow. A catalog selection has the exact same shape as
 * a parsed search's `parameters` (metrics.metrics / filters.filters / places), so it goes
 * through `parametersToSegments` -- the same reconstruction SearchResults.js already uses -- and
 * lands via `pendingSegments`, the same one-shot channel a clicked template uses. That's what
 * gets it resolved into real, highlighted, editable tokens in the bar.
 *
 * "Apply" then runs the search (Explorer Figma, Data Catalog: "Clicking the 'Apply' button will
 * run the query"), exactly as SearchBar.js's own submit does: the query text, the segments it
 * came from, and a navigation to the results page. `parameters` is cleared unless the query is
 * the one already on screen -- SearchResults.js doesn't re-run that one, so nothing would refill
 * them. Used by every catalog entry point (SearchBar, LandingPage, "You're viewing…") so they
 * stay in sync.
 */
export function useDataCatalogSearch() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const resultsQuery = useSelector((state) => state.search.resultsQuery);

  const applySelection = (selections) => {
    const segments = parametersToSegments(selections);
    const text = parametersToQueryText(selections);

    dispatch(setPendingSegments(segments));
    dispatch(setQuery(text));
    dispatch(setLastSearchSegments(segments));
    if (text !== resultsQuery) {
      dispatch(resetParameters());
    }
    navigate(`/search-results?query=${encodeURIComponent(text)}`);
  };

  return { applySelection };
}
